/**
 * KYC (Know Your Customer) Service - Plaid Identity Verification
 * Handles identity verification for regulatory compliance
 * Updated to use Plaid IDV instead of Stripe Identity
 */

import apiDefaults from './api';
import { ApiResponse } from '../types';

export interface KYCInfo {
  status: 'none' | 'not_started' | 'active' | 'pending_review' | 'verified' | 'failed' | 'expired';
  sessionId?: string;
  plaidStatus?: string;
  steps?: any;
  verifiedAt?: string;
  createdAt?: string;
  message?: string;
}

export interface KYCSessionResponse {
  success: boolean;
  alreadyVerified?: boolean;
  message?: string;
  sessionId?: string;
  url?: string;
  link_token?: string;  // For Plaid Link SDK flow
  shareable_url?: string;  // Fallback URL for popup flow
  id?: string;  // Plaid IDV session ID
  status?: string;
  expiration?: string;
}

class KYCService {
  /**
   * Create Plaid Identity Verification session
   */
  async createKycSession(): Promise<ApiResponse<KYCSessionResponse>> {
    try {
      const response = await apiDefaults.post('/plaid/idv/session');
      return response as ApiResponse<KYCSessionResponse>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create KYC session',
        message: 'Failed to create KYC verification session'
      };
    }
  }

  /**
   * Get KYC status from Plaid IDV
   */
  async getKycStatus(): Promise<ApiResponse<KYCInfo>> {
    try {
      const response = await apiDefaults.get('/plaid/idv/status');
      return response as ApiResponse<KYCInfo>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get KYC status',
        message: 'Failed to get KYC status'
      };
    }
  }

  /**
   * Retry a failed or expired IDV session
   */
  async retryKyc(): Promise<ApiResponse<KYCSessionResponse>> {
    try {
      const response = await apiDefaults.post('/plaid/idv/retry');
      return response as ApiResponse<KYCSessionResponse>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to retry KYC',
        message: 'Failed to retry KYC verification'
      };
    }
  }

  /**
   * Abandon the latest IDV session and reset status so the user can restart.
   */
  async abandonKyc(): Promise<ApiResponse<{ status: string; message?: string }>> {
    try {
      const response = await apiDefaults.post('/plaid/idv/abandon');
      return response as ApiResponse<{ status: string; message?: string }>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to restart KYC',
        message: 'Failed to restart identity verification'
      };
    }
  }

  /**
   * Get specific verification session details
   * Uses Plaid IDV status endpoint
   */
  async getSessionDetails(sessionId: string): Promise<ApiResponse<any>> {
    try {
      // Use Plaid IDV status endpoint - sessionId is tracked server-side
      const response = await apiDefaults.get('/plaid/idv/status');
      return response as ApiResponse<any>;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get session details',
        message: 'Failed to retrieve verification session details'
      };
    }
  }

  /**
   * Check if user can perform investment actions
   */
  async canInvest(): Promise<boolean> {
    try {
      const response = await this.getKycStatus();
      return response.success && response.data?.status === 'verified';
    } catch (error) {
      console.error('Error checking investment eligibility:', error);
      return false;
    }
  }

  /**
   * Get human-readable status message
   */
  getStatusMessage(status: string): string {
    const messages: { [key: string]: string } = {
      none: 'Identity verification not started',
      not_started: 'Identity verification not started',
      active: 'Verification in progress',
      pending_review: 'Your verification is being reviewed',
      verified: 'Identity verified successfully',
      failed: 'Verification failed - please try again',
      expired: 'Verification session expired - please try again'
    };
    
    return messages[status] || 'Unknown verification status';
  }

  /**
   * Check if status allows investment
   */
  isVerified(status?: string): boolean {
    return status === 'verified';
  }

  /**
   * Check if status requires user action
   */
  requiresAction(status?: string): boolean {
    return status === 'created' || status === 'requires_input';
  }

  /**
   * Check if status is final (verified, failed, or canceled)
   */
  isFinalStatus(status?: string): boolean {
    return status === 'verified' || status === 'failed' || status === 'canceled';
  }
}

export const kycService = new KYCService();
export default kycService;
